import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/customSupabaseClient';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AlertCircle,
  Users as UsersIcon,
  ArrowUpDown,
  UserPlus,
  Package
} from 'lucide-react';
import {
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  LineChart
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import AssignProgramForm from '@/components/AssignProgramForm';
import RemoveProgramForm from '@/components/RemoveProgramForm';

const PageSkeleton = () => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[...Array(3)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-4 rounded" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-7 w-16 mb-1" />
          </CardContent>
        </Card>
      ))}
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-64 mt-1" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-72 w-full" />
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-36" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-64 w-full" />
      </CardContent>
    </Card>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-card p-2 text-card-foreground">
        <p className="font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground">{`New Users: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

const Users = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fetchUsersData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('user_programs_overview')
        .select('*');
      if (error) throw error;
      setUsersData(data || []);
    } catch (err) {
      setError('Failed to fetch user data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  const handleProgramAssignedOrRemoved = () => {
    fetchUsersData();
  };

  const kpis = useMemo(() => {
    if (!usersData || usersData.length === 0) {
      return { totalUsers: 0, totalAssignedPrograms: 0, newUsersThisWeek: 0, userRegistrations: [] };
    }
    
    const uniqueUserIds = new Set(usersData.map(u => u.user_id));
    const totalUsers = uniqueUserIds.size;
    const totalAssignedPrograms = usersData.length;

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const newUsersThisWeek = usersData.filter(u => u.purchased_at && new Date(u.purchased_at) >= oneWeekAgo).length;
    
    const registrationCounts = usersData.reduce((acc, user) => {
        if(user.purchased_at) {
          const date = new Date(user.purchased_at).toLocaleDateString('en-CA');
          acc[date] = (acc[date] || 0) + 1;
        }
        return acc;
    }, {});
    
    const userRegistrations = Object.entries(registrationCounts)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      totalUsers,
      totalAssignedPrograms,
      newUsersThisWeek,
      userRegistrations,
    };
  }, [usersData]);

  const columns = useMemo(() => [
    {
      accessorKey: 'user_name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <img
            className="h-8 w-8 rounded-full object-cover"
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(row.original.user_name || 'User')}&background=random`}
            alt={row.original.user_name}
          />
          <div className="flex flex-col">
            <span className="font-medium">{row.original.user_name || 'N/A'}</span>
            {row.original.user_id && (
              <Link
                to={`/user-activity/${row.original.user_id}`}
                className="text-xs text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
              >
                View profile &amp; manage
              </Link>
            )}
          </div>
        </div>
      ),
    },
    { accessorKey: 'user_email', header: 'Email' },
    { accessorKey: 'program_name', header: 'Program' },
    { 
        accessorKey: 'purchased_at', 
        header: ({ column }) => (
            <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                Purchased At <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
        ),
        cell: ({ row }) => row.original.purchased_at ? new Date(row.original.purchased_at).toLocaleString() : 'N/A' 
    },
    { 
        accessorKey: 'last_sign_in_at', 
        header: ({ column }) => (
            <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                Last Sign In <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
        ),
        cell: ({ row }) => row.original.last_sign_in_at ? new Date(row.original.last_sign_in_at).toLocaleString() : 'N/A' 
    },
    { 
        accessorKey: 'is_active', 
        header: 'Status',
        cell: ({ row }) => <Badge variant={row.original.is_active ? 'success' : 'secondary'}>{row.original.is_active ? 'Active' : 'Inactive'}</Badge>
    },
    { 
        accessorKey: 'payment_status', 
        header: 'Payment',
        cell: ({ row }) => <Badge variant={row.original.payment_status === 'paid' ? 'success' : 'warning'}>{row.original.payment_status}</Badge>
    },
  ], []);

  const table = useReactTable({
    data: usersData,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <>
      <Helmet>
        <title>Users - My Movement Space</title>
      </Helmet>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">User Program Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage program enrollments and assignments for all users.</p>
        </div>
        <Input
          placeholder="Filter users..."
          value={globalFilter ?? ''}
          onChange={e => setGlobalFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {loading ? <PageSkeleton /> : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 gap-3">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium text-destructive">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <UsersIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{kpis.totalUsers}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Assigned Programs</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{kpis.totalAssignedPrograms}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">New Enrollments (Last 7 Days)</CardTitle>
                <UserPlus className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{kpis.newUsersThisWeek}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <section id="assign-program-to-user-section">
              <AssignProgramForm onProgramAssigned={handleProgramAssignedOrRemoved} />
            </section>
            <section id="remove-program-from-user-section">
              <RemoveProgramForm onProgramRemoved={handleProgramAssignedOrRemoved} />
            </section>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Program Enrollments Over Time</CardTitle>
              <CardDescription>New user program enrollment trends.</CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={kpis.userRegistrations} margin={{ top: 5, right: 30, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'hsl(220, 70%, 50%)', strokeWidth: 1 }} />
                  <Line type="monotone" dataKey="count" stroke="hsl(220, 70%, 50%)" strokeWidth={2} dot={{ r: 3, fill: "hsl(220, 70%, 50%)" }} activeDot={{ r: 6 }} name="New Enrollments" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All User Programs</CardTitle>
              <CardDescription>{table.getFilteredRowModel().rows.length} program enrollments found.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map(hg => (
                      <TableRow key={hg.id}>
                        {hg.headers.map(h => (
                          <TableHead key={h.id}>{h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}</TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map(row => (
                        <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                          {row.getVisibleCells().map(cell => (
                            <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow><TableCell colSpan={columns.length} className="h-24 text-center">No results.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-end space-x-2 py-4">
                <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Next</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default Users;
